import { Intent } from "@blueprintjs/core";
import axios from "axios";
import classNames from "classnames";
import { Button, LinkButton } from "components/buttons";
import { ButtonSave } from "components/buttons/ButtonSave";
import { DateToLocal } from "components/dates";
import { ShowHide } from "components/layout";
import { Breadcrumbs } from "components/navigation";
import { VerticalLinkGroup } from "components/navigation/VerticalLinkGroup";
import { AlertConfirm } from "components/notifications/AlertConfirm";
import { AlertUnsavedData } from "components/notifications/AlertUnsavedData";
import { NotificationInline } from "components/notifications/NotificationInline";
import { NotificationToaster } from "components/notifications/NotificationToaster";
import { UserAccess } from "components/user-access/UserAccess";
import { HelperFunctions } from "helpers";
import { useDirtyData } from "hooks/useDirtyData";
import { DocumentEntityListing } from "pages/documents/listing";
import { DocumentTypeHistory } from "pages/documents/settings/document-types/edit/document-type-history/DocumentTypeHistory";
import { DocumentTypeAdditionalDetails } from "pages/documents/settings/document-types/shared/document-type-additional-settings/DocumentTypeAdditionalSettings";
import { DocumentTypeDetails } from "pages/documents/settings/document-types/shared/document-type-details";
import { PageRestricted } from "pages/errors/page-restricted/PageRestricted";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { DocumentsService } from "services/DocumentsService";

export function EditDocumentType(props) {
    const { id } = useParams();

    const baseRoute = '/document-settings/types';

    const defaultDocumentType = {
        name: "",
        area: null,
        dataRetention: null
    }

    const [documentType, setDocumentType] = useState(defaultDocumentType);
    const [originalDocumentType, setOriginalDocumentType] = useState(defaultDocumentType);

    const isOrganisationDocumentType = documentType?.area === 'Organisation';

    const routes = useMemo(() => {
        const newRoutes = [
            { location: `${baseRoute}/${id}/details`, title: "Details" },
            { location: `${baseRoute}/${id}/settings`, title: "Settings" }
        ];

        if (isOrganisationDocumentType) {
            newRoutes.push({ location: `${baseRoute}/${id}/documents`, title: "Documents" });
        }

        newRoutes.push({ location: `${baseRoute}/${id}/history`, title: "History" });

        return newRoutes;
    }, [documentType])

    const [documentOptions, setDocumentOptions] = useState({
        documentAreas: []
    })

    const [loading, setLoading] = useState(true);
    const [isValid, setIsValid] = useState(true);
    const isReadOnly = useMemo(() => !!documentType.dateToBeDeleted, [documentType]);
    const [saving, setSaving] = useState(false);

    const [showRecoverConfirm, setShowRecoverConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [invalidType, setInvalidType] = useState(false);

    const isDocumentTypeDirty = useDirtyData(originalDocumentType, documentType);

    const loadDocumentType = () => {
        setLoading(true);
        axios.all([
            DocumentsService.getDocumentType(id),
            DocumentsService.getDocumentOptions()
        ]).then(axios.spread((documentTypeResponse, documentOptionsResponse) => {
            setDocumentType(documentTypeResponse);
            setOriginalDocumentType(HelperFunctions.deepClone(documentTypeResponse));
            setDocumentOptions(documentOptionsResponse);
            
            setLoading(false);
        }),
        (error) => {
            const noAccessCode = 403;

            if (error.status === noAccessCode) {
                setInvalidType(true);
            } else {
                NotificationToaster.show(Intent.DANGER, "Could not load document type. Please try again.");
            }
        })
    }

    useEffect(() => {
        loadDocumentType();
    }, [id]);

    const saveDocument = () => {
        setSaving(true);

        if (!isValid) {
            NotificationToaster.show(Intent.WARNING, "There are errors on the page that need your attention");
            setSaving(false);
            return;
        }

        DocumentsService.updateDocumentType(id, documentType).then(
            () => {
                setSaving(false);
                setOriginalDocumentType({...documentType});
                NotificationToaster.show(Intent.SUCCESS, "Document type updated successfully.");
            },
            (err) => {
                setSaving(false);
                NotificationToaster.show(Intent.DANGER, err);
            }
        );
    }

    const renderSaveButton = () => (
        <div className="row button-row">
            <ButtonSave
                id="btn-document-edit-save"
                onClick={saveDocument}
                disabled={saving}
                simpleDisabled={isReadOnly || !isValid}
                loading={loading}
            ></ButtonSave>
            <ShowHide
                evaluator={!!documentType.dateToBeDeleted}
                show={<Button
                    intent={Intent.NONE}
                    text="Recover"
                    onClick={() => setShowRecoverConfirm(true)}
                    loading={loading}
                    disabled={isDocumentTypeDirty || saving}
                />}
                hide={<Button
                    intent={Intent.DANGER}
                    text="Delete"
                    loading={loading}
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isReadOnly || isDocumentTypeDirty || saving}
                />}
            />
        </div>);

    const recoverType = () => {
        setShowRecoverConfirm(false);
        setSaving(true);
        DocumentsService.recoverDocumentType(id).then(() => {
            setSaving(false);
            NotificationToaster.show(Intent.SUCCESS, "Document type recovered successfully.");
            loadDocumentType();
        })
    }

    const deleteType = () => {
        setShowDeleteConfirm(false);
        setSaving(true);
        DocumentsService.deleteDocumentType(id).then(() => {
            setSaving(false);
            NotificationToaster.show(Intent.SUCCESS, "Document type deleted successfully.");
            loadDocumentType();
        })
    }

    return (
        <UserAccess perform={['Documents:Manage']}
            yes={() => (
                <ShowHide
                    evaluator={invalidType}
                    show={(
                        <div className="row">
                            <Breadcrumbs items={props.breadcrumbs} />
                            <NotificationInline
                                allowClose={false}
                                show
                                text="You do not have permission to view this type"
                                intent="danger">
                            </NotificationInline>
                            <div>
                                <LinkButton intent="primary" text="Back to document types" href="/document-settings/types" id="return-document-types-listing" />
                            </div>
                        </div>
                    )}
                    hide={(
                        <Fragment>
                            <div className="row">

                                <Breadcrumbs items={props.breadcrumbs} />

                                <div className={classNames("inline-items", "space-medium", "spacer-bottom", { "bp6-skeleton": loading })}>
                                    <h1 className="no-margin">{originalDocumentType.name} Settings</h1>
                                </div>
                                <NotificationInline intent={Intent.WARNING} show={!!documentType.dateToBeDeleted} text={<span>Document type will be deleted on the <DateToLocal format="DD/MM/YYYY">{documentType.dateToBeDeleted}</DateToLocal></span>} />
                            </div>
                            <div className="layout-cols">
                                <div className="col side-col">
                                    <div className="row">
                                        <VerticalLinkGroup links={routes}></VerticalLinkGroup>
                                    </div>
                                </div>
                                <div className="col content-col">
                                    <Routes>
                                        <Route path="details" element={<><DocumentTypeDetails
                                                documentType={documentType}
                                                onChange={setDocumentType}
                                                documentAreas={documentOptions.documentAreas}
                                                onValidationChange={setIsValid}
                                                saving={saving}
                                                loading={loading}
                                            />
                                            {renderSaveButton()}</>} />
                                        <Route path="settings" element={<><DocumentTypeAdditionalDetails
                                                documentType={documentType}
                                                onChange={setDocumentType}
                                                onValidationChange={setIsValid}
                                                saving={saving}
                                                loading={loading}
                                            />
                                            {renderSaveButton()}</>} />
                                        <Route path="documents" element={<div className="row">
                                                <h2>Documents</h2>
                                                <DocumentEntityListing
                                                    loading={loading}
                                                    documentTypeId={documentType.id}
                                                    entityType={"Organisation"}
                                                    entityId={null}
                                                    showTitle={false}
                                                    filterName='organisation-documents'
                                                />
                                            </div>} />
                                        <Route path="history" element={<DocumentTypeHistory documentTypeId={id} />} />
                                        <Route path="" element={<Navigate to={`${baseRoute}/${id}/details`} replace />} />
                                    </Routes>
                                </div>
                            </div>
                            <AlertUnsavedData
                                isDirty={isDocumentTypeDirty && !isReadOnly}>
                            </AlertUnsavedData>
                            <AlertConfirm
                                title="Delete Document Type"
                                isOpen={showDeleteConfirm}
                                onConfirm={deleteType}
                                onCancel={() => setShowDeleteConfirm(false)}
                                confirmButtonIntent="danger"
                                confirmButtonText="Delete">
                                <p>Are you sure you want to delete this document type? This action is only reversible for a limited time. After that, the document type and all associated documents will be permanently removed from Vision.</p>
                            </AlertConfirm>
                            <AlertConfirm
                                title="Recover Document Type"
                                isOpen={showRecoverConfirm}
                                onConfirm={recoverType}
                                onCancel={() => setShowRecoverConfirm(false)}
                                confirmButtonIntent="primary"
                                confirmButtonText="Recover">
                                <p>Are you sure you want to recover this document type?</p>
                            </AlertConfirm>
                        </Fragment>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />     
    );
}